import { useEffect } from 'react'
import { getSsoSession } from 'auth/ssoClient'
import { cfg } from 'config/runtimeConfig'

/**
 * White-label branding, the way the Sales module does it.
 *
 * Kit19 is resold under partner brands, so nothing here may say "Kit19" on its
 * own: the logo, favicon and name come from the SSO session (the partner's Logo /
 * FaviconIcon and the company name), with the deployment's config.js as the only
 * fallback. A partner host leaves those blank; the Kit19 host points them at
 * Kit19's own assets. The tab title is composed exactly as Sales composes it:
 * "<page> | <login name>".
 */
export interface Brand {
  name: string
  logo: string
  favicon: string
}

export function getBrand(): Brand {
  const session = getSsoSession()
  return {
    name: session.companyName || String(cfg('REACT_APP_BRAND_NAME') ?? ''),
    logo: session.logo || String(cfg('REACT_APP_BRAND_LOGO_URL') ?? ''),
    favicon: session.faviconIcon || String(cfg('REACT_APP_BRAND_FAVICON_URL') ?? ''),
  }
}

/** Sets the tab title and favicon for a page; call it once per page component. */
export function useBrand(pageTitle: string): void {
  useEffect(() => {
    const session = getSsoSession()
    const login = session.loginName || session.displayName
    document.title = login ? `${pageTitle} | ${login}` : pageTitle

    const { favicon } = getBrand()
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    // "data:," is the placeholder index.html ships so no /favicon.ico is requested
    link.href = favicon || 'data:,'
  }, [pageTitle])
}
